import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faPlus, faMinus } from "@fortawesome/free-solid-svg-icons";
import {
  petstarSubscribe,
  petstarCustomSubscribe,
} from "../../api/PetstarSubscribe";
import CustomAmountModal from "./CustomAmountModal";
import SignupPopup from "./SignupPopup";
import LoginSignupModal from "../shared/LoginSignupModal/LoginSignupModal";

export default function PetStar() {
  const [localUserData, setLocalUserData] = useState(null);
  const [isCustomAmountModalVisible, setIsCustomAmountModalVisible] =
    useState(false);
  const [showAffiliateSignup, setShowAffiliateSignup] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [loginOrSignup, setLoginOrSignup] = useState("");
  const [affiliateId, setAffiliateId] = useState("");
  const [email, setEmail] = useState("");
  const [expandedFaq, setExpandedFaq] = useState(0);

  const userData = useSelector((state) => state.userData.userData);
  const location = useLocation();

  useEffect(() => {
    if (Object.keys(userData).length !== 0) {
      setLocalUserData(JSON.parse(userData));
    }
  }, [userData]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const params = new URLSearchParams(location.search);
    if (params.has("affiliate_id")) {
      setShowAffiliateSignup(true);
      setAffiliateId(params.get("affiliate_id"));
    }
  }, [location]);

  const handleSubClick = async (priceId) => {
    try {
      if (priceId) {
        if (localUserData) {
          const response = await petstarSubscribe({
            price_id: priceId,
            affiliate_id: affiliateId,
          });
          if (response.redirect_url) {
            window.location.href = response.redirect_url;
          }
        } else {
          alert("Please sign up or log in to make a donation.");
        }
      }
    } catch (error) {
      console.error("There was a problem with the subscribe operation:", error);
    }
  };

  const handleCustomAmt = () => {
    if (localUserData) {
      setIsCustomAmountModalVisible(true);
    } else {
      alert("Please sign up or log in to make a custom donation.");
    }
  };

  const handleSubmitCustomAmount = async (amountStr) => {
    try {
      const amount = parseFloat(amountStr, 10);
      if (!isNaN(amount) && amount > 0) {
        const response = await petstarCustomSubscribe({
          amount: amount,
          affiliate_id: affiliateId,
        });
        if (response.redirect_url) {
          window.location.href = response.redirect_url;
        }
      } else {
        alert("Please enter a valid, positive amount.");
      }
    } catch (error) {
      console.error(
        "There was a problem with the custom subscribe operation:",
        error,
      );
    }
    setIsCustomAmountModalVisible(false);
  };

  const openModal = (type) => {
    setLoginOrSignup(type);
    setModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setModalOpen(false);
    document.body.style.overflow = "";
  };

  const handleFormSuccess = (success) => {
    if (success) {
      window.location.reload();
    }
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    // TODO: Implement email subscription
    setEmail("");
  };

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? -1 : index);
  };

  const faqData = [
    {
      question: "What is the PetStar Club?",
      answer:
        "The PetStar Club is a monthly subscription program that allows you to make a recurring donation to help animals in need. Your monthly contribution goes directly to nonprofit animal rescues worldwide.",
    },
    {
      question: "How much can I donate each month?",
      answer:
        "You can choose from our preset amounts ($10, $25, $35, $50, $100) or enter a custom amount that works for your budget.",
    },
    {
      question: "What rewards do I get as a PetStar?",
      answer:
        "PetStar members receive monthly updates, free merchandise based on giving level, discounts from corporate sponsors, and exclusive contests and promotions.",
    },
    {
      question: "Can I cancel my subscription anytime?",
      answer:
        "Yes, you can cancel your PetStar subscription at any time through your account dashboard. There are no cancellation fees or penalties.",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="flex min-h-[600px]">
        {/* Left Side - Text Content (50%) */}
        <div className="w-1/2 bg-blue py-8 md:py-16 px-4 md:px-8 pt-24 md:pt-32 flex items-center">
          <div className="max-w-lg mx-auto text-white text-left">
            <h1
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 md:mb-8 text-white leading-tight !font-inter"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              Welcome to The PetStar Club
            </h1>
            <p className="text-lg mb-6 leading-relaxed text-left">
              Become a monthly hero for rescue animals by joining the PetStar
              Club! Your monthly subscription pools donations for a greater
              impact, providing essential support like vet care, food, and
              resources for nonprofit animal rescues worldwide. 100% of every
              dollar goes directly to helping animals in need.
            </p>
            <p className="text-lg mb-8 text-left">
              Subscribe to help all of our nonprofit partners while earning
              awesome rewards!
            </p>

            {/* Email Subscription */}
            <form
              onSubmit={handleEmailSubmit}
              className="flex flex-col sm:flex-row gap-4 mb-6"
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter Email"
                className="flex-1 px-3 py-4 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange"
                required
              />
              <button
                type="submit"
                className="bg-orange text-white px-6 py-4 rounded-lg font-semibold hover:bg-orange-dark transition-colors"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>

        {/* Right Side - Image (50%) */}
        <div className="w-1/2 flex items-center">
          <img
            src="https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/petstar-page/petstar.jpg"
            alt="Cat and dog"
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      {/* Donations Section */}
      <div className="py-8 md:py-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <span className="text-blue text-sm font-medium uppercase tracking-wide">
            DONATIONS
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-orange mt-2 mb-12">
            How much would you like to help each month?
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-8">
            {[
              {
                amount: "$10/month",
                description: "Help feed a shelter animal",
              },
              { amount: "$25/month", description: "Support veterinary care" },
              { amount: "$35/month", description: "Provide medical supplies" },
              { amount: "$50/month", description: "Fund rescue operations" },
              { amount: "$100/month", description: "Sponsor multiple animals" },
              { amount: "Custom", description: "Enter Amount", isCustom: true },
            ].map((option, index) => (
              <div
                key={index}
                className={`p-6 rounded-lg border-2 cursor-pointer transition-all ${
                  option.isCustom
                    ? "border-dashed border-gray-300 hover:border-orange"
                    : "border-gray-200 hover:border-orange hover:shadow-lg"
                }`}
                onClick={
                  option.isCustom
                    ? handleCustomAmt
                    : () =>
                        handleSubClick(
                          `priceId_for_${option.amount
                            .split("/")[0]
                            .replace("$", "")}`,
                        )
                }
              >
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {option.amount}
                </h3>
                <p className="text-gray-600 text-sm">{option.description}</p>
              </div>
            ))}
          </div>

          <button
            onClick={() => handleSubClick("priceId_for_35")}
            className="bg-blue text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-dark transition-colors"
          >
            Donate
          </button>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="py-8 md:py-16 px-4 md:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <span className="text-blue text-sm font-medium uppercase tracking-wide">
            HOW IT WORKS
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-orange mt-2 mb-12">
            Be The Star In The Lives of All The Animals
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-24 h-24 bg-blue rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl"></span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Place Donations
              </h3>
              <p className="text-gray-600">
                Select donation amount and we'll direct it to where it's needed
                most
              </p>
            </div>

            <div className="text-center">
              <div className="w-24 h-24 bg-blue rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl"></span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Personalized Updates
              </h3>
              <p className="text-gray-600">
                See the impact you made and hear from our volunteers
              </p>
            </div>

            <div className="text-center">
              <div className="w-24 h-24 bg-blue rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl"></span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Rewards Program
              </h3>
              <p className="text-gray-600">
                A special thank you from us with unique discounts and free
                merchandise!
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* What You Earn Section */}
      <div className="py-16 px-8" style={{ backgroundColor: "#1b3a5d" }}>
        <div className="max-w-8xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Images */}
            <div className="flex gap-4">
              <img
                src="https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/Group+1000006592.png"
                alt="PetStar image 1"
                className="w-[40%] h-110 object-cover rounded-lg"
              />
              <img
                src="https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/Group+1000006593.png"
                alt="PetStar image 2"
                className="w-[40%] h-110 object-cover rounded-lg"
              />
            </div>

            {/* Right Side - Content */}
            <div className="text-white text-left">
              <span className="text-white text-base font-medium uppercase tracking-wide">
                WHAT YOU EARN AS A PETSTAR
              </span>
              <h2 className="text-4xl md:text-5xl font-semibold text-orange mt-2 mb-8">
                Monthly Updates
              </h2>

              <ul className="space-y-5">
                {[
                  "Statistic of how many animals/shelters you've impacted",
                  "Monthly email updates of Sponsor a Pet's Impact",
                  "Notification of your animal's birthday",
                  "Recognition of outstanding donors",
                  "Volunteer spotlight - Learn how others are making an impact in their community",
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-4">
                    <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center mt-1 flex-shrink-0">
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="text-blue-600 text-sm"
                      />
                    </div>
                    <span className="text-lg">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Rewards Section */}
      <div className="py-16 px-8">
        <div className="max-w-8xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Content */}
            <div className="text-left">
              <span className="text-blue text-sm font-medium uppercase tracking-wide">
                REWARDS
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-orange mt-2 mb-8 text-left">
                Earn Rewards For Your Donation
              </h2>

              <div className="grid grid-cols-2 gap-4">
                {[
                  "Free Merchandise (based on giving level)",
                  "Discounts from our Corporate Sponsorships",
                  "Contests for whoever refers the most amount of new PetStar members",
                  "Discounts for our products on pet supplies",
                ].map((reward, index) => (
                  <div
                    key={index}
                    className="p-4 bg-white rounded-lg border-2 border-black"
                  >
                    <p className="text-gray-700 text-sm text-left">{reward}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Side - Image */}
            <div className="flex justify-center">
              <img
                src="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/SAP+Consulting+Hi-Res+Images/Group+1000006608+(1).png"
                alt="Woman with dog"
                className="rounded-lg shadow-lg max-w-full h-auto"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Affiliates Section */}
      <div
        className="py-16 px-8 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage:
            "url('https://sap-app-prod.s3.us-west-2.amazonaws.com/web_images/maurice-dt-yXSHUYqDwx8-unsplash.jpg')",
        }}
      >
        <div className="max-w-7xl mx-auto text-left">
          <div className="p-8">
            <span className="text-white text-sm font-medium uppercase tracking-wide">
              AFFILIATES Program
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-2 mb-6">
              Click Here to Learn More About Our Affiliates and Earn Money From
              Donations
            </h2>
            <a
              href="/affiliates"
              className="inline-block bg-orange text-white px-8 py-3 rounded-lg font-semibold hover:bg-orange-dark transition-colors"
            >
              Our Affiliates Program
            </a>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="py-8 md:py-16 px-4 md:px-8">
        <div className="max-w-8xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Left Side - Heading (2/5 width) */}
            <div className="lg:col-span-2 lg:pr-8">
              <span className="text-blue text-sm font-medium uppercase tracking-wide">
                FREQUENTLY ASKED QUESTIONS
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-orange mt-2">
                Common Questions About Our PetStar Club
              </h2>
            </div>

            {/* Right Side - FAQ Items (3/5 width) */}
            <div className="lg:col-span-3 space-y-4">
              {faqData.map((faq, index) => (
                <div key={index} className="border border-gray-200 rounded-lg">
                  <button
                    className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    onClick={() => toggleFaq(index)}
                  >
                    <span className="font-semibold text-gray-900 text-lg">
                      {faq.question}
                    </span>
                    <FontAwesomeIcon
                      icon={expandedFaq === index ? faMinus : faPlus}
                      className="text-gray-500 text-xl"
                    />
                  </button>
                  {expandedFaq === index && (
                    <div className="p-6 pt-0">
                      <p className="text-gray-600 text-lg leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {isModalOpen && (
        <LoginSignupModal
          loginOrSignup={loginOrSignup}
          isOpen={isModalOpen}
          onClose={closeModal}
          onFormSuccess={handleFormSuccess}
        />
      )}

      {isCustomAmountModalVisible && (
        <CustomAmountModal
          onClose={() => setIsCustomAmountModalVisible(false)}
          onSubmit={handleSubmitCustomAmount}
        />
      )}

      {showAffiliateSignup && (
        <SignupPopup onClose={() => setShowAffiliateSignup(false)} />
      )}
    </div>
  );
}
